<template>
  <div class="moderation-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-shield-check</v-icon>
                Content Moderation Dashboard
                <span class="page-inline-subtitle">AI-powered content safety monitoring and review</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn
                  color="primary"
                  :disabled="!selectedItems.length"
                  @click="bulkActionDialog = true"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-check-all</v-icon>
                  Bulk Action ({{ selectedItems.length }})
                </v-btn>
                <v-btn 
                  color="info" 
                  @click="fetchModerationResults"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
                <v-btn 
                  color="error" 
                  :disabled="!selectedItems.length"
                  @click="deleteQueueDialog = true"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-delete</v-icon>
                  Delete Queue ({{ selectedItems.length }})
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3" v-for="stat in stats" :key="stat.title">
          <v-card 
            class="stats-card"
            elevation="4"
            hover
          >
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper" :class="`${stat.color}--bg`">
                <v-icon size="28" color="white">{{ getStatIcon(stat.title) }}</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stat.value }}</div>
                <div class="stats-label">{{ stat.title }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Moderation Queue -->
      <v-row>
        <v-col cols="12">
          <v-card class="moderation-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      Moderation Queue
                    </h2>
                    <div class="results-count" v-if="totalResults > 0">
                      <div class="count-display">
                        <span class="count-number">{{ totalResults }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Status Filter Chips Section -->
                  <div class="filters-section">
                    <v-chip-group v-model="selectedFilter" class="status-chips">
                      <v-chip
                        v-for="filter in moderationFilters"
                        :key="filter.value"
                        :color="filter.color"
                        :value="filter.value"
                        filter
                        outlined
                        small
                        class="modern-chip"
                      >
                        {{ filter.text }}
                      </v-chip>
                    </v-chip-group>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="fetchModerationResults()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Refresh
                      </v-btn>
                      
                      <v-btn
                        v-if="selectedItems.length > 0"
                        color="#10b981"
                        @click="bulkActionDialog = true"
                        class="filter-export-btn clean-btn"
                        small
                      >
                        <v-icon small left>mdi-check-all</v-icon>
                        Bulk ({{ selectedItems.length }})
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                v-model="selectedItems"
                :headers="headers"
                :items="moderationResults"
                :loading="loading"
                :server-items-length="totalResults"
                :options.sync="options"
                @update:options="fetchModerationResults"
                show-select
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (options.page - 1) * options.itemsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.post.title`]="{ item }">
                <div class="text-truncate" style="max-width: 200px;">
                  {{ item.post.title }}
                </div>
              </template>

              <template v-slot:[`item.ai_recommendation`]="{ item }">
                <v-chip
                  :color="getRecommendationColor(item.ai_recommendation)"
                  dark
                  small
                >
                  {{ item.ai_recommendation.toUpperCase() }}
                </v-chip>
              </template>

              <template v-slot:[`item.overall_score`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-progress-circular
                      :value="item.overall_score * 100"
                      :color="getRiskColor(item.overall_score)"
                      :size="40"
                      :width="4"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ (item.overall_score * 100).toFixed(0) }}%
                    </v-progress-circular>
                  </template>
                  <div>
                    <div>Toxicity: {{ (item.toxicity_score * 100).toFixed(1) }}%</div>
                    <div>Spam: {{ (item.spam_score * 100).toFixed(1) }}%</div>
                    <div>Inappropriate: {{ (item.inappropriate_score * 100).toFixed(1) }}%</div>
                  </div>
                </v-tooltip>
              </template>

              <template v-slot:[`item.manual_decision`]="{ item }">
                <v-chip
                  v-if="item.manual_decision"
                  :color="getRecommendationColor(item.manual_decision)"
                  dark
                  small
                >
                  {{ item.manual_decision.toUpperCase() }}
                </v-chip>
                <v-chip v-else color="grey" outlined small>
                  PENDING
                </v-chip>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  @click="viewDetails(item)"
                  color="info"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  v-if="!item.manual_decision"
                  icon
                  small
                  @click="makeDecision(item)"
                  color="primary"
                >
                  <v-icon>mdi-gavel</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="options.page"
                  :length="Math.ceil(totalResults / options.itemsPerPage)"
                  :total-visible="7"
                  color="primary"
                  @input="fetchModerationResults"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Decision Dialog -->
    <v-dialog v-model="decisionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Make Moderation Decision</v-card-title>
        <v-card-text v-if="selectedResult">
          <div class="mb-4">
            <strong>Post:</strong> {{ selectedResult.post.title }}<br>
            <strong>AI Recommendation:</strong> 
            <v-chip
              :color="getRecommendationColor(selectedResult.ai_recommendation)"
              dark
              small
            >
              {{ selectedResult.ai_recommendation.toUpperCase() }}
            </v-chip>
          </div>

          <v-select
            v-model="decisionForm.decision"
            :items="decisionOptions"
            label="Your Decision"
            required
          ></v-select>

          <v-textarea
            v-model="decisionForm.notes"
            label="Notes (Optional)"
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="decisionDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!decisionForm.decision"
            @click="submitDecision"
          >
            Submit Decision
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Action Dialog -->
    <v-dialog v-model="bulkActionDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>Bulk Moderation Action</v-card-title>
        <v-card-text>
          <div class="mb-4">
            Selected {{ selectedItems.length }} items for bulk action.
          </div>

          <v-select
            v-model="bulkForm.decision"
            :items="decisionOptions"
            label="Decision for All Selected"
            required
          ></v-select>

          <v-textarea
            v-model="bulkForm.notes"
            label="Notes (Applied to All)"
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bulkActionDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!bulkForm.decision"
            @click="submitBulkAction"
          >
            Apply to All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card v-if="selectedResult">
        <v-card-title>
          Moderation Details
          <v-spacer></v-spacer>
          <v-chip
            :color="getRecommendationColor(selectedResult.ai_recommendation)"
            dark
          >
            AI: {{ selectedResult.ai_recommendation.toUpperCase() }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="8">
              <h4>{{ selectedResult.post.title }}</h4>
              <p class="mt-2">{{ selectedResult.post.content }}</p>
              
              <div class="mt-4">
                <strong>Author:</strong> {{ selectedResult.post.user.first_name }} {{ selectedResult.post.user.last_name }}<br>
                <strong>Type:</strong> {{ selectedResult.post.type }}<br>
                <strong>Created:</strong> {{ formatDate(selectedResult.post.created_at) }}
              </div>

              <div v-if="selectedResult.ai_reasoning" class="mt-4">
                <h4>AI Reasoning:</h4>
                <p class="text-body-2">{{ selectedResult.ai_reasoning }}</p>
              </div>

              <div v-if="selectedResult.manual_decision" class="mt-4">
                <h4>Human Decision:</h4>
                <v-chip
                  :color="getRecommendationColor(selectedResult.manual_decision)"
                  dark
                >
                  {{ selectedResult.manual_decision.toUpperCase() }}
                </v-chip>
                <div v-if="selectedResult.moderator_notes" class="mt-2">
                  <strong>Notes:</strong> {{ selectedResult.moderator_notes }}
                </div>
                <div class="mt-1">
                  <strong>Moderated by:</strong> {{ selectedResult.moderator?.first_name }} {{ selectedResult.moderator?.last_name }}
                  at {{ formatDate(selectedResult.moderated_at) }}
                </div>
              </div>
            </v-col>
            
            <v-col cols="4">
              <v-card outlined>
                <v-card-title class="text-h6">Risk Analysis</v-card-title>
                <v-card-text>
                  <div class="mb-3">
                    <strong>Overall Risk:</strong>
                    <v-progress-linear
                      :value="selectedResult.overall_score * 100"
                      :color="getRiskColor(selectedResult.overall_score)"
                      height="25"
                      class="mt-1"
                    >
                      {{ (selectedResult.overall_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Toxicity:</strong>
                    <v-progress-linear
                      :value="selectedResult.toxicity_score * 100"
                      color="red"
                      height="20"
                      class="mt-1"
                    >
                      {{ (selectedResult.toxicity_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Spam:</strong>
                    <v-progress-linear
                      :value="selectedResult.spam_score * 100"
                      color="orange"
                      height="20"
                      class="mt-1"
                    >
                      {{ (selectedResult.spam_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Inappropriate:</strong>
                    <v-progress-linear
                      :value="selectedResult.inappropriate_score * 100"
                      color="purple"
                      height="20"
                      class="mt-1"
                    >
                      {{ (selectedResult.inappropriate_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!selectedResult.manual_decision"
            color="primary"
            @click="makeDecision(selectedResult)"
          >
            Make Decision
          </v-btn>
          <v-btn text @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Queue Dialog -->
    <v-dialog v-model="deleteQueueDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h5 error--text">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          Delete Moderation Queue
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <p class="body-1 mb-3">
              Are you sure you want to delete <strong>{{ selectedItems.length }}</strong> 
              moderation {{ selectedItems.length === 1 ? 'result' : 'results' }} from the queue?
            </p>
            <div class="warning-box pa-3 mb-3">
              <v-icon color="warning" class="mr-2">mdi-information</v-icon>
              <span class="warning-text">
                This action will permanently remove the selected moderation analysis data and cannot be undone.
                The associated posts will remain but will lose their moderation status.
              </span>
            </div>
            <v-checkbox
              v-model="deleteConfirmation"
              label="I understand this action cannot be undone"
              color="error"
              class="mt-2"
            ></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="submitting"
            :disabled="!deleteConfirmation"
            @click="confirmDeleteQueue"
          >
            <v-icon left>mdi-delete</v-icon>
            Delete Queue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
      class="notification-snackbar"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: "ModerationIndex",
  data() {
    return {
      loading: false,
      submitting: false,
      moderationResults: [],
      totalResults: 0,
      selectedItems: [],
      selectedFilter: null,
      decisionDialog: false,
      bulkActionDialog: false,
      detailsDialog: false,
      deleteQueueDialog: false,
      deleteConfirmation: false,
      selectedResult: null,
      statsData: {},
      decisionForm: {
        decision: null,
        notes: ''
      },
      bulkForm: {
        decision: null,
        notes: ''
      },
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Post', value: 'post.title', sortable: false, width: '200px' },
        { text: 'Author', value: 'post.user.first_name', sortable: false, width: '150px' },
        { text: 'AI Recommendation', value: 'ai_recommendation', sortable: true, width: '140px' },
        { text: 'Risk Score', value: 'overall_score', sortable: true, width: '120px' },
        { text: 'Human Decision', value: 'manual_decision', sortable: true, width: '130px' },
        { text: 'Created', value: 'created_at', sortable: true, width: '140px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      moderationFilters: [
        { text: 'All', value: null, color: 'grey' },
        { text: 'Pending Review', value: 'pending', color: 'orange' },
        { text: 'High Risk', value: 'high_risk', color: 'red' },
        { text: 'AI: Approve', value: 'approve', color: 'green' },
        { text: 'AI: Reject', value: 'reject', color: 'red' },
        { text: 'AI: Flag', value: 'flag', color: 'purple' },
      ],
      decisionOptions: [
        { text: 'Approve', value: 'approve' },
        { text: 'Reject', value: 'reject' },
        { text: 'Flag for Review', value: 'flag' },
      ],
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      }
    };
  },
  computed: {
    stats() {
      return [
        {
          title: 'Pending Review',
          value: this.statsData.pending_moderation || 0,
          color: 'orange'
        },
        {
          title: 'High Risk Posts',
          value: this.statsData.high_risk_posts || 0,
          color: 'red'
        },
        {
          title: 'Approved Today',
          value: this.statsData.approved_posts || 0,
          color: 'green'
        },
        {
          title: 'Avg Risk Score',
          value: this.statsData.avg_overall_score ? 
            (this.statsData.avg_overall_score * 100).toFixed(1) + '%' : '0%',
          color: 'blue'
        }
      ];
    }
  },
  watch: {
    selectedFilter() {
      this.options.page = 1;
      this.fetchModerationResults();
    }
  },
  mounted() {
    this.fetchModerationResults();
    this.fetchStats();
  },
  methods: {
    async fetchModerationResults() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage
        };

        if (this.selectedFilter === 'pending') {
          params.manual_decision = 'pending';
        } else if (this.selectedFilter === 'high_risk') {
          params.high_risk = true;
        } else if (['approve', 'reject', 'flag'].includes(this.selectedFilter)) {
          params.ai_recommendation = this.selectedFilter;
        }

        const response = await axios.get('moderation', { params });
        
        this.moderationResults = response.data.data.data;
        this.totalResults = response.data.data.total;
      } catch (error) {
        this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
        
        // Show error message for fetch failures
        if (error.response?.status !== 401) { // Don't show error for auth failures
          this.showErrorMessage(
            error.response?.data?.message || 'Failed to load moderation results'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const response = await axios.get('moderation/stats');
        this.statsData = response.data.data;
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        if (error.response?.status !== 401) {
          this.showErrorMessage('Failed to load statistics');
        }
      }
    },

    makeDecision(result) {
      this.selectedResult = result;
      this.decisionForm = {
        decision: null,
        notes: ''
      };
      this.decisionDialog = true;
      this.detailsDialog = false;
    },

    async submitDecision() {
      this.submitting = true;
      try {
        await axios.post(`moderation/${this.selectedResult.id}/decision`, this.decisionForm);
        
        this.decisionDialog = false;
        this.fetchModerationResults();
        this.fetchStats();
        this.$store.dispatch('errors/setErrors', {});
        
        // Show success message
        this.showSuccessMessage('Moderation decision recorded successfully!');
      } catch (error) {
        this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
        
        // Show error message
        this.showErrorMessage(
          error.response?.data?.message || 'Failed to record decision'
        );
      } finally {
        this.submitting = false;
      }
    },

    async submitBulkAction() {
      this.submitting = true;
      try {
        const payload = {
          result_ids: this.selectedItems.map(item => item.id),
          decision: this.bulkForm.decision,
          notes: this.bulkForm.notes
        };

        await axios.post('moderation/bulk-decision', payload);
        
        this.bulkActionDialog = false;
        this.selectedItems = [];
        this.fetchModerationResults();
        this.fetchStats();
        this.$store.dispatch('errors/setErrors', {});
        
        // Show success message
        this.showSuccessMessage(`Bulk action completed successfully for ${payload.result_ids.length} items!`);
      } catch (error) {
        this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
        
        // Show error message
        this.showErrorMessage(
          error.response?.data?.message || 'Failed to complete bulk action'
        );
      } finally {
        this.submitting = false;
      }
    },

    viewDetails(result) {
      this.selectedResult = result;
      this.detailsDialog = true;
    },

    getRecommendationColor(recommendation) {
      const colors = {
        approve: 'green',
        reject: 'red',
        flag: 'purple'
      };
      return colors[recommendation] || 'grey';
    },

    getRiskColor(score) {
      if (score >= 0.7) return 'red';
      if (score >= 0.4) return 'orange';
      return 'green';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
    },

    getStatIcon(title) {
      const icons = {
        'Pending Review': 'mdi-clock-outline',
        'High Risk Posts': 'mdi-alert-circle',
        'Approved Today': 'mdi-check-circle',
        'Avg Risk Score': 'mdi-chart-line'
      };
      return icons[title] || 'mdi-information';
    },

    closeDeleteDialog() {
      this.deleteQueueDialog = false;
      this.deleteConfirmation = false;
    },

    async confirmDeleteQueue() {
      if (!this.deleteConfirmation || !this.selectedItems.length) {
        return;
      }

      this.submitting = true;
      try {
        // Get selected moderation result IDs
        const moderationIds = this.selectedItems.map(item => item.id);
        
        // Delete moderation results in batches
        const deletePromises = moderationIds.map(id => 
          axios.delete(`moderation/${id}`)
        );
        
        await Promise.all(deletePromises);
        
        // Clear selection and close dialog
        this.selectedItems = [];
        this.closeDeleteDialog();
        
        // Refresh data
        await this.fetchModerationResults();
        await this.fetchStats();
        
        this.showSuccessMessage(
          `Successfully deleted ${moderationIds.length} moderation ${moderationIds.length === 1 ? 'result' : 'results'} from the queue`
        );
      } catch (error) {
        console.error('Delete queue error:', error);
        this.showErrorMessage(
          error.response?.data?.message || 'Failed to delete moderation queue items'
        );
      } finally {
        this.submitting = false;
      }
    },
    
    showSuccessMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 5000
      };
    },
    
    showErrorMessage(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 7000
      };
    }
  }
};
</script>

<style scoped>
.moderation-dashboard {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 24px !important;
  height: 44px !important;
}

/* Stats Cards */
.stats-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
  background: white;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stats-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

/* Moderation Table Card */
.moderation-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

.status-chips {
  display: flex;
  gap: 8px;
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Export Button */
.filter-export-btn.clean-btn {
  background: #10b981 !important;
  color: white !important;
  border: none !important;
}

.filter-export-btn.clean-btn:hover {
  background: #059669 !important;
}

.modern-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease;
}

.modern-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

.modern-data-table >>> .v-data-table__selected {
  background: rgba(102, 126, 234, 0.1) !important;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Dialogs */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.v-dialog >>> .v-card-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  font-weight: 600;
}

.v-dialog >>> .v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Progress Indicators */
.v-progress-circular {
  font-weight: 600 !important;
}

.v-progress-linear {
  border-radius: 4px !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filters-single-row {
    gap: 16px;
  }
}

@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filter-actions {
    justify-content: center;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
  
  .stats-content {
    padding: 16px !important;
  }
  
  .stats-icon-wrapper {
    width: 48px;
    height: 48px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
}

/* Delete Queue Dialog */
.warning-box {
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 8px;
}

.warning-text {
  color: #e65100;
  font-weight: 500;
  font-size: 14px;
}

/* Notification Snackbar */
.notification-snackbar {
  z-index: 10000;
}

.notification-snackbar .v-snack__wrapper {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.notification-snackbar .v-snack__content {
  font-weight: 500;
  font-size: 14px;
  padding: 16px 20px;
}
</style>